import * as React from 'react'

import { useIsInView, type UseIsInViewOptions } from '@/hooks/use-is-in-view'

type CountingNumberProps = Omit<React.ComponentProps<'span'>, 'children'> & {
	number: number
	fromNumber?: number
	padStart?: boolean
	decimalSeparator?: string
	decimalPlaces?: number
	delay?: number
	/** Animation length in ms when `animate` is true. */
	duration?: number
	/** When false, the value updates instantly. When true, animates from `fromNumber` to `number`. */
	animate?: boolean
	/** Bump this on each intended animation so a new run starts even if `animate` stays true. */
	animationKey?: number | string
	onAnimationComplete?: () => void
} & UseIsInViewOptions

function easeOutCubic(t: number): number {
	return 1 - (1 - t) ** 3
}

function CountingNumber({
	ref,
	number,
	fromNumber = 0,
	padStart = false,
	inView = false,
	inViewMargin = '0px',
	inViewOnce = true,
	decimalSeparator = '.',
	decimalPlaces = 0,
	delay = 0,
	duration = 800,
	animate = false,
	animationKey,
	onAnimationComplete,
	...props
}: CountingNumberProps) {
	const { ref: localRef, isInView } = useIsInView(ref as React.Ref<HTMLSpanElement>, {
		inView,
		inViewOnce,
		inViewMargin,
	})

	const numberStr = number.toString()
	const decimals = typeof decimalPlaces === 'number' ? decimalPlaces : numberStr.includes('.') ? (numberStr.split('.')[1]?.length ?? 0) : 0
	const finalIntLength = Math.floor(Math.abs(number)).toString().length

	const formatValue = React.useCallback(
		(val: number) => {
			let out = decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString()
			if (decimals > 0) out = out.replace('.', decimalSeparator)
			if (padStart) {
				const [intPart, fracPart] = out.split(decimalSeparator)
				const paddedInt = (intPart ?? '').padStart(finalIntLength, '0')
				out = fracPart ? `${paddedInt}${decimalSeparator}${fracPart}` : paddedInt
			}
			return out
		},
		[decimalSeparator, decimals, finalIntLength, padStart],
	)

	const writeValue = React.useCallback(
		(val: number) => {
			if (localRef.current) {
				localRef.current.textContent = formatValue(val)
			}
		},
		[formatValue, localRef],
	)

	const onCompleteRef = React.useRef(onAnimationComplete)
	onCompleteRef.current = onAnimationComplete

	React.useLayoutEffect(() => {
		if (!isInView) {
			return
		}

		if (!animate) {
			writeValue(number)
			return
		}

		const startValue = fromNumber
		const endValue = number
		let frameId = 0
		let startTime = 0

		writeValue(startValue)

		const tick = (now: number) => {
			if (!startTime) {
				startTime = now
			}
			const elapsed = now - startTime
			const t = duration <= 0 ? 1 : Math.min(1, elapsed / duration)
			const current = startValue + (endValue - startValue) * easeOutCubic(t)
			writeValue(current)

			if (t < 1) {
				frameId = window.requestAnimationFrame(tick)
				return
			}

			writeValue(endValue)
			onCompleteRef.current?.()
		}

		const timeoutId = window.setTimeout(() => {
			frameId = window.requestAnimationFrame(tick)
		}, delay)

		return () => {
			window.clearTimeout(timeoutId)
			window.cancelAnimationFrame(frameId)
		}
	}, [animate, animationKey, delay, duration, fromNumber, isInView, number, writeValue])

	return <span ref={localRef} {...props} />
}

export { CountingNumber, type CountingNumberProps }
